import { v4 } from "uuid";
import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { isApiError } from "@/services/Api";
import { caseClientUiConfigService, caseService } from "@/services";
import { agentSession } from "@/services/AgentClient";
import { ioaOverlapsAgreementPeriod } from "@/services/UtilitiesService";

import { useIoaStore } from "@/stores/ioa-store";
import { useTagStore } from "@/stores/tags-store";
import { useBrandStore } from "@/stores/brand-store";
import { useNotesStore } from "@/stores/notes-store";
import { usePointStore } from "@/stores/point-store";
import { useFiltersStore } from "@/stores/filters-store";
import { useMessageStore } from "@/stores/message-store";
import { useSnippetStore } from "@/stores/snippets-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useQuarantineStore } from "@/stores/quarantine-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useAgentChatStore } from "@/stores/agent-chat-store";
import { useCaseSecurityStore } from "@/stores/case-security-store";
import { useEvidenceCountStore } from "@/stores/evidence-count-store";

import { pollingEvents } from "@/stores/plugins/polling-events";

import type AgreementPeriod from "@/models/AgreementPeriod";
import type CaseViewerConfig from "@/models/CaseViewerConfig";
import type InstanceOfAdvice from "@/models/InstanceOfAdvice";
import type { MigrationPlan, PlanRequest, PlanResponse } from "@/models/RecordMigration";

export const useCaseStore = defineStore("case", {
  state: (): State => ({
    _loadingCaseName: undefined,
    checksets: [],
    config: { agreementPeriods: [] },
    moduleStatus: ModuleStatus.Initial,
    sessionLinkId: undefined,
    sessionLinkResumed: false,
    backLinkedConversationId: undefined,
    migrationPlans: [],
    toolModeFindingReference: undefined,
  }),
  getters: {
    selectedCaseName(): string | undefined {
      return this.currentRoute.params.casename as string | undefined;
    },
    isMigrationSource(): boolean {
      return this.migrationPlans.some(
        (plan) => plan.sourceCaseReference.toLowerCase() === this.selectedCaseName?.toLowerCase(),
      );
    },
    isMigrationDestination(): boolean {
      return this.migrationPlans.some(
        (plan) => plan.destinationCaseReference.toLowerCase() === this.selectedCaseName?.toLowerCase(),
      );
    },
  },
  actions: {
    async onCaseNameChange(): Promise<void> {
      const caseName = this.selectedCaseName;
      if (this._loadingCaseName === caseName && this.moduleStatus === ModuleStatus.Loading) return;

      this._loadingCaseName = caseName;
      this.setCaseStatus(ModuleStatus.Loading);

      try {
        await this.onCaseReferenceLoaded();
        await useCaseFileStore().onSelectedFileChange();
      } catch (e) {
        console.error(e);
      }

      if (this.selectedCaseName !== caseName) return;

      this._loadingCaseName = undefined;
      this.setCaseStatus(ModuleStatus.Ready);
    },
    async updateAgreementPeriod(arg: AgreementPeriod | AgreementPeriod[]): Promise<void> {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || this.selectedCaseName == null) return;

      const map: Record<string, AgreementPeriod> = {};
      for (const period of this.config.agreementPeriods) map[period.id] = period;
      for (const period of Array.isArray(arg) ? arg : [arg]) map[period.id] = period;

      const agreementPeriods = Object.values(map);

      await caseClientUiConfigService
        .Save(selectedClient, selectedBrand, this.selectedCaseName, { agreementPeriods })
        .then(() => this.setCaseConfig({ agreementPeriods }));
    },
    async removeIoaFromAgreementPeriods(instanceReference: string): Promise<void> {
      let shouldUpdate = false;
      const cleaned: AgreementPeriod[] = [];

      for (const period of this.config.agreementPeriods) {
        if (!(period.ioas?.includes(instanceReference) ?? false)) cleaned.push(period);
        else {
          shouldUpdate = true;
          cleaned.push({ ...period, ioas: period.ioas!.filter((ref) => ref !== instanceReference) });
        }
      }

      if (shouldUpdate) await this.updateAgreementPeriod(cleaned);
    },
    async sanitizeAgreementPeriodIoaAssociations(): Promise<void> {
      const ioaStore = useIoaStore();
      if (ioaStore.moduleStatus !== ModuleStatus.Ready) return;

      const ioaMap = new Map<string, InstanceOfAdvice>();
      for (const ioa of ioaStore.instanceOfAdvices) ioaMap.set(ioa.instanceReference, ioa);

      let shouldUpdate = false;
      const cleaned = this.config.agreementPeriods.map((period) => {
        const ioas: string[] = [];

        period.ioas?.forEach((ref) => {
          if (ioaMap.has(ref) && ioaOverlapsAgreementPeriod(period, ioaMap.get(ref)!)) ioas.push(ref);
          else shouldUpdate = true;
        });

        return { ...period, ioas };
      });

      if (shouldUpdate) await this.updateAgreementPeriod(cleaned);
    },
    async deleteAgreementPeriod(agreementPeriod: AgreementPeriod): Promise<void> {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || this.selectedCaseName == null) return;

      const agreementPeriods = [...new Set(this.config.agreementPeriods)];
      const index: number = this.config.agreementPeriods.findIndex((f) => f.id === agreementPeriod.id);
      if (index < 0) {
        return;
      } else {
        agreementPeriods.splice(index, 1);
      }

      await caseClientUiConfigService
        .Save(selectedClient, selectedBrand, this.selectedCaseName, { agreementPeriods })
        .then(() => this.setCaseConfig({ agreementPeriods }));
    },
    async getCaseCheckSets(): Promise<void> {
      try {
        const caseReference = this.selectedCaseName;
        const { selectedBrand, selectedClient } = useBrandStore();

        if (caseReference == null || selectedClient == null || selectedBrand == null) return;

        const ccs = await caseService.caseCheckSets(selectedClient, selectedBrand, caseReference);
        this.setCaseCheckSets(ccs);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.setCaseCheckSets([]);
          return;
        }

        useMessageStore().updateMessage({
          type: "error",
          title: "Case Check Sets",
          message: "Error Loading Case Check Sets",
          autoHide: false,
          showReload: true,
        });

        throw e;
      }
    },
    async getCaseConfig(): Promise<void> {
      try {
        const caseReference = this.selectedCaseName;
        const { selectedBrand, selectedClient } = useBrandStore();
        if (caseReference == null || selectedClient == null || selectedBrand == null) return;

        const caseConfig = await caseClientUiConfigService.Get(selectedClient, selectedBrand, caseReference);
        this.setCaseConfig(caseConfig);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.setCaseConfig(undefined);
          return;
        }

        useMessageStore().updateMessage({
          type: "error",
          title: "Case Configuration",
          message: "Error Loading Case Configuration",
          autoHide: false,
          showReload: true,
        });

        throw e;
      }
    },

    async loadMigrationPlans(): Promise<void> {
      const caseReference = this.selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (caseReference == null || selectedClient == null || selectedBrand == null) return;

      try {
        const caseMetadata = await caseService.GetCaseMetadata(selectedClient, selectedBrand, caseReference);
        const migrations = caseMetadata.value.migration;
        this.migrationPlans =
          migrations != null && typeof migrations === "object"
            ? Object.values(migrations as Record<string, MigrationPlan>)
            : [];
      } catch (e) {
        this.migrationPlans = [];
        if (!isApiError(e) || (e.status !== 404 && e.status !== 204)) console.error(e);
      }
    },

    async planRecordMigration(request: PlanRequest): Promise<PlanResponse> {
      try {
        const { selectedBrand, selectedClient } = useBrandStore();
        if (selectedBrand == null || selectedClient == null) {
          throw new Error("Cannot plan record migration without a selected brand and client.");
        }

        return await caseService.planRecordMigration(selectedClient, selectedBrand, request);
      } finally {
        pollingEvents.restart();
      }
    },

    async migrateCaseRecords(plan: MigrationPlan, token: string): Promise<void> {
      try {
        const { selectedBrand, selectedClient } = useBrandStore();
        if (selectedBrand == null || selectedClient == null) {
          throw new Error("Cannot migrate case records without a selected brand and client.");
        }

        await caseService.migrateRecords(selectedClient, selectedBrand, { plan, token });
      } finally {
        pollingEvents.restart();
      }
    },

    // Mutations
    // The brand screen's agent chat: this tab's session for the brand (the same one its cases use). A
    // session already in this tab keeps the case its agent is working on, as it can carry on with it from
    // here; only a new one is started with no case.
    openBrandSession() {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedClient == null || selectedBrand == null) return;

      const sessionLinkKey = `agentSessionLink:${selectedClient}/${selectedBrand}`;
      const storedSessionLinkId = sessionStorage.getItem(sessionLinkKey);
      const sessionLinkId = storedSessionLinkId ?? v4();
      if (this.sessionLinkId !== sessionLinkId) {
        this.sessionLinkResumed = storedSessionLinkId != null;
        this.sessionLinkId = sessionLinkId;
      }
      sessionStorage.setItem(sessionLinkKey, sessionLinkId);

      if (storedSessionLinkId == null) {
        this._moveAgentSession(sessionLinkId, selectedClient, selectedBrand, undefined);
      } else {
        useAgentChatStore().noteBrandScreen();
      }
    },
    // "New chat": a fresh session for the brand in this tab, on the case the agent was working on (else
    // the one on screen, or none). The old conversation is left as it is on the server, just no longer
    // bound to this tab.
    startNewAgentSession() {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedClient == null || selectedBrand == null) return;

      // Read before the new session link resets what the chat store knows.
      const agentChatStore = useAgentChatStore();
      const agentCase = agentChatStore.agentCase ?? this.selectedCaseName;

      const sessionLinkId = v4();
      sessionStorage.setItem(`agentSessionLink:${selectedClient}/${selectedBrand}`, sessionLinkId);
      this.backLinkedConversationId = undefined;
      this.sessionLinkResumed = false;
      this.sessionLinkId = sessionLinkId;

      this._moveAgentSession(sessionLinkId, selectedClient, selectedBrand, agentCase, false);
    },
    // Moves the session (and the agent conversation bound to it) onto caseReference, or off any case.
    // A turn already in flight reads the session's case from its tool calls, so the move waits for it
    // to finish — unless the tab has moved on again by then. followsTab: caseReference is the case on
    // screen, so the move is dropped if the screen has changed; otherwise it is the agent's own case.
    _moveAgentSession(
      sessionLinkId: string,
      clientName: string,
      brand: string,
      caseReference: string | undefined,
      followsTab = true,
    ) {
      const agentChatStore = useAgentChatStore();
      void agentChatStore
        .whenIdle()
        .then(async () => {
          if ((followsTab && this.selectedCaseName !== caseReference) || this.sessionLinkId !== sessionLinkId) return;
          await agentSession.setConversationContext(sessionLinkId, { clientName, brand, caseReference });
          agentChatStore.noteCaseChange(caseReference ?? null);
        })
        .catch(console.warn);
    },
    async onCaseReferenceLoaded() {
      const caseReference = this.selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (caseReference != null && selectedClient != null && selectedBrand != null) {
        // Reuse this tab's session for the brand, so the agent chat bound to it survives a reload and
        // carries on across cases. A brand change reloads the page and starts a session of its own.
        const sessionLinkKey = `agentSessionLink:${selectedClient}/${selectedBrand}`;
        const storedSessionLinkId = sessionStorage.getItem(sessionLinkKey);
        let sessionLinkId = storedSessionLinkId ?? v4();
        const { conversationId, finding, ...restQuery } = this.currentRoute.query;

        if (typeof conversationId === "string") {
          // Keep the chat in its loading state until the back-link settles, rather than exposing an interim session.
          this.sessionLinkId = undefined;
          this.sessionLinkResumed = storedSessionLinkId != null;
          try {
            sessionLinkId = (await agentSession.backLink(sessionLinkId, conversationId)) ?? sessionLinkId;
            this.sessionLinkResumed = true;
            this.backLinkedConversationId = conversationId;
          } finally {
            this.sessionLinkId = sessionLinkId;
          }
          this.router.replace({ query: restQuery });
        } else {
          this.backLinkedConversationId = undefined;
          this.sessionLinkResumed = storedSessionLinkId != null;
          this.sessionLinkId = sessionLinkId;
        }
        sessionStorage.setItem(sessionLinkKey, sessionLinkId);

        if (typeof finding === "string") this.toolModeFindingReference = finding;

        this._moveAgentSession(sessionLinkId, selectedClient, selectedBrand, caseReference);

        // We are setting a case reference, we could load case specific data
        /* This would split it out from pages and allow the store to build itself rather than being
           triggered from individual pages */
        const ioaStorePromise = useIoaStore().load();
        const caseConfigPromise = this.getCaseConfig();
        const caseCheckSetsPromise = this.getCaseCheckSets();

        const promises = [
          useEvidenceStore().loadCategories(),
          useEvidenceStore().loadEvidence(),
          useMetadataStore().load(),
          ioaStorePromise,
          useAnnotationStore().load(),
          useTagStore().load(),
          useQuarantineStore().load(),
          useSnippetStore().load(),
          useNotesStore().load(),
          useEvidenceCountStore().load(),
          useCaseSecurityStore().load(),
          this.loadMigrationPlans(),
          caseConfigPromise,
          caseCheckSetsPromise.then(() => usePointStore().load()),
          Promise.all([ioaStorePromise, caseConfigPromise]).then(() => this.sanitizeAgreementPeriodIoaAssociations()),
        ];

        await Promise.allSettled(promises);

        useFiltersStore().selectFilterSet(undefined);
        return;
      }

      // We are removing a case, we should clear the case specific data. The session link is kept, so
      // the agent chat carries on into the next case.
      useAnnotationStore().reset();
      useCaseStore().reset();
      useCaseFileStore().reset();
      useEvidenceStore().reset();
      useEvidenceCountStore().reset();
      useFiltersStore().reset();
      useIoaStore().reset();
      useMetadataStore().reset();
      useNotesStore().reset();
      useQuarantineStore().reset();
      useSnippetStore().reset();
      useTagStore().reset();
      usePointStore().reset();
    },
    setCaseConfig(payload: CaseViewerConfig | undefined): void {
      this.config = payload ?? { agreementPeriods: [] };
    },
    setCaseStatus(payload: ModuleStatus): void {
      this.moduleStatus = payload;
    },
    setCaseCheckSets(checkSets: string[]): void {
      const configuredCheckSets = useBrandStore().viewerConfig?.checks?.sets;

      // Null check sets means just use what is attached
      // Empty arrays just use what is attached as well
      // Empty strings, just use what is attached as well
      if (
        configuredCheckSets == null ||
        (Array.isArray(configuredCheckSets) && configuredCheckSets.length === 0) ||
        configuredCheckSets === ""
      ) {
        this.checksets = checkSets;
        return;
      }

      // Get the checks sets included in the array or in the checkset string
      const filteredCheckSets = checkSets.filter((cs) => {
        if (Array.isArray(configuredCheckSets)) {
          return configuredCheckSets.some((ccs) => ccs.toLowerCase() === cs.toLowerCase());
        } else {
          return cs.toLowerCase() === configuredCheckSets.toLowerCase();
        }
      });

      // We have checksets attached to the case, but they will be hidden
      // Pop a console warning out so we can track why they are not showing easily
      if (filteredCheckSets.length === 0 && checkSets.length > 0) {
        console.warn(
          "No check sets are configured to be shown, but there are check sets attached to the case. Please check your configuration.",
        );
      }

      this.checksets = filteredCheckSets;
    },
    reset(): void {
      this.config = { agreementPeriods: [] };
      this.checksets = [];
      this.migrationPlans = [];
    },
  },
});
interface State {
  _loadingCaseName: string | undefined;
  sessionLinkId: string | undefined;
  // True when sessionLinkId was reused from an earlier load, so a chat may already be bound to it.
  sessionLinkResumed: boolean;
  // The agent conversation this tab's session was back-linked to by the link it was opened from, if any.
  backLinkedConversationId: string | undefined;
  checksets: string[];
  config: CaseViewerConfig;
  moduleStatus: ModuleStatus;
  migrationPlans: MigrationPlan[];
  toolModeFindingReference: string | undefined;
}
